import createClient from "openapi-fetch";
import type { paths, components } from "./api-schema";

export const client = createClient<paths>({
  credentials: "include",
  headers: { "x-workbench": "1" },
});
export type Schema = components["schemas"];
export type Work = Schema["WorkOut"];
export type Project = Schema["ProjectOut"];
export type Revision = Schema["RevisionOut"];
export type Source = Schema["FragmentOut"];
export type User = Schema["UserOut"];

export class ApiError extends Error {
  constructor(message: string, public status: number) { super(message); }
  // Screens print errors with String(error): the estimator sees the message, not «Error: …».
  override toString() { return this.message; }
}

function errorMessage(detail: unknown, status: number) {
  if (typeof detail === "string" && detail) return detail;
  if (status === 401) return "Войдите в рабочее место";
  if ([502, 503, 504].includes(status)) return `Нет ответа от сервера (HTTP ${status}): сервер, вероятно, перезапускается. Обновите страницу через минуту; перед повтором действия проверьте, не сохранилось ли оно.`;
  if (detail != null) return JSON.stringify(detail);
  return `Не удалось выполнить действие (HTTP ${status}). Проверьте состояние задачи и повторите действие.`;
}

export async function result<T>(
  promise: Promise<{ data?: T; error?: unknown; response: Response }>,
): Promise<T> {
  const response = await promise.catch((error: unknown) => {
    if (error instanceof TypeError) throw new ApiError("Связь с сервером прервалась. Проверьте соединение и повторите действие. Если отправка уже началась, сначала проверьте результат и историю.", 0);
    throw error;
  });
  if (response.error || !response.response.ok) {
    const detail = (response.error as { detail?: unknown })?.detail;
    throw new ApiError(errorMessage(detail, response.response.status), response.response.status);
  }
  return response.data as T;
}

// Reads are safe to repeat. A dropped connection or an empty gateway (a deploy restarts the API for
// about 15 s) is retried with growing pauses; an answer the server gave (4xx, 500) is shown at once.
const TRANSIENT = new Set([0, 502, 503, 504]);

export function retryTransient(failureCount: number, error: unknown) {
  return failureCount < 5 && error instanceof ApiError && TRANSIENT.has(error.status);
}

export function retryDelay(attempt: number) {
  return Math.min(1000 * 2 ** attempt, 16000);
}

export function amount(value: string | number | null | undefined) {
  return value == null
    ? "—"
    : Number(value).toLocaleString("ru-RU", { maximumFractionDigits: 8 });
}
export const statusLabel: Record<string, string> = {
  approved: "Подтверждено",
  pending: "Не проверено",
  needs_review: "Проверить",
  rejected: "Исключено",
};
export const categories: Record<string, string[]> = {
  АР: ["Стены", "Полы", "Потолки", "Двери", "Окна", "Откосы", "Фасад"],
  ЭОМ: ["Освещение", "Розетки и выключатели", "Щиты", "Кабельные линии"],
};

export async function call<T>(path: string, init?: { method?: string; body?: unknown; form?: FormData }): Promise<T> {
  const headers: Record<string, string> = { "x-workbench": "1" };
  let body: BodyInit | undefined;
  if (init?.form) body = init.form;
  else if (init?.body !== undefined) {
    headers["content-type"] = "application/json";
    body = JSON.stringify(init.body);
  }
  const response = await fetch(path, { method: init?.method ?? "GET", credentials: "include", headers, body }).catch(() => {
    throw new ApiError("Связь с сервером прервалась. Проверьте соединение и историю перед повтором. Введённые значения сохранены в форме.", 0);
  });
  if (!response.ok) {
    let detail: unknown;
    try {
      const data = await response.json();
      detail = data?.detail;
    } catch { detail = undefined; }
    throw new ApiError(errorMessage(detail, response.status), response.status);
  }
  return response.json() as Promise<T>;
}
